import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SendInvoiceEmailDialog } from './SendInvoiceEmailDialog';
import type { Invoice } from '@/api/types';

interface InvoiceCardProps {
  invoice: Invoice;
  onMarkPaid: (invoiceId: string) => void;
  onMarkUnpaid: (invoiceId: string) => void;
  getAccessToken: () => Promise<string>;
}

function formatCurrency(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

function formatDate(dateStr: string, locale: string): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isOverdue(dueDate: string, paymentStatus: string): boolean {
  return paymentStatus === 'unpaid' && new Date(dueDate) < new Date();
}

export function InvoiceCard({
  invoice,
  onMarkPaid,
  onMarkUnpaid,
  getAccessToken,
}: Readonly<InvoiceCardProps>) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'et' ? 'et-EE' : 'en-US';
  const overdue = isOverdue(invoice.due_date, invoice.payment_status);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          {invoice.invoice_number}
          {invoice.client_name && (
            <span className="ml-2 text-muted-foreground font-normal">{invoice.client_name}</span>
          )}
        </CardTitle>
        {overdue && <Badge variant="destructive">{t('invoice.overdue')}</Badge>}
        {!overdue && invoice.payment_status === 'paid' && (
          <Badge variant="default" className="bg-green-600">
            {t('invoice.paid')}
          </Badge>
        )}
        {!overdue && invoice.payment_status !== 'paid' && (
          <Badge variant="secondary">{t('invoice.unpaid')}</Badge>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold" data-testid="invoice-amount">
          {formatCurrency(invoice.amount, invoice.currency, locale)}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {t('invoice.issuedAt')}: {formatDate(invoice.issued_at, locale)}
          {' · '}
          {t('invoice.due')}: {formatDate(invoice.due_date, locale)}
        </div>
        {invoice.payment_status === 'paid' && invoice.paid_at && (
          <div className="text-xs text-muted-foreground mt-1">
            {t('invoice.paidAt')}: {formatDate(invoice.paid_at, locale)}
            {invoice.payment_method &&
              ` · ${t('invoice.paymentMethod')}: ${invoice.payment_method}`}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          {invoice.payment_status === 'unpaid' ? (
            <Button size="sm" onClick={() => onMarkPaid(invoice.id)}>
              {t('invoice.markPaid')}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onMarkUnpaid(invoice.id)}>
              {t('invoice.markUnpaid')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowEmailDialog(true)}
            data-testid="invoice-send-email-btn"
          >
            <Mail className="mr-1 h-4 w-4" />
            {t('invoice.sendEmail')}
          </Button>
        </div>
      </CardContent>

      {showEmailDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-lg bg-background shadow-lg">
            <SendInvoiceEmailDialog
              invoiceId={invoice.id}
              defaultEmail={null}
              clientName={invoice.client_name}
              getAccessToken={getAccessToken}
              onClose={() => setShowEmailDialog(false)}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
